/**
 * Einheitliche Offline-Session (currentUser / currentBusiness) für Login und Registrierung.
 */

import { kvGet, kvSet, kvRemove, kvSetPersisted } from '../infrastructure/storage/kvAccess';
import { clearBusinessLogoutIntent } from '../utils/businessLogoutFlag';
import { persistRoleForBusiness } from '../utils/roleStorage';
import { normalizeBusinessStorageId } from '../utils/sharedCore';

export function buildOfflineSessionUser(business) {
  if (!business?.id) return null;
  // Always storage slug (strip one leading business_) so login matches entity_* / KV keys
  const id = normalizeBusinessStorageId(business.id) || String(business.id);
  return {
    type: 'business',
    user: {
      id,
      email: business.email,
      user_metadata: {
        business_name: business.business_name,
        owner_name: business.owner_name,
        business_type: business.business_type
      }
    },
    business: {
      ...business,
      id,
      auth_user_id: id,
      logo: business.logo || null
    },
    offline: true
  };
}

export function buildOfflineCurrentBusiness(business) {
  if (!business?.id) return null;
  const id = normalizeBusinessStorageId(business.id) || String(business.id);
  return {
    ...business,
    id,
    businessId: id,
    business_name: business.business_name || business.name,
    name: business.business_name || business.name,
    logo: business.logo || null
  };
}

/** Legacy-Shape aus alter Registrierung in kanonische Session umwandeln. */
export function normalizeStoredSessionUser(user) {
  if (!user || typeof user !== 'object') return user;
  if (user.type !== 'business') return user;
  if (user.user?.id && user.business?.id) return user;
  if (user.business?.id) return buildOfflineSessionUser(user.business);
  return user;
}

/**
 * Schreibt currentUser/currentBusiness, Rolle, optional Bootstrap + authStateChanged.
 * @param {object} business
 * @param {{
 *   awaitBootstrap?: boolean,
 *   dispatchAuthEvent?: boolean,
 *   assignDefaultRole?: boolean,
 *   clearRole?: boolean,
 *   persist?: boolean
 * }} [options]
 */
export async function establishOfflineBusinessSession(business, options = {}) {
  const {
    awaitBootstrap = true,
    dispatchAuthEvent = false,
    assignDefaultRole = false,
    persist = false
  } = options;
  // Default: clear role unless assigning manager or caller opts out (cashier handoff)
  const clearRole =
    options.clearRole !== undefined ? options.clearRole : !assignDefaultRole;

  // Keep login business id sticky — never silent-relink to another bucket.
  const effectiveBusiness = business;

  const user = buildOfflineSessionUser(effectiveBusiness);
  const currentBusiness = buildOfflineCurrentBusiness(effectiveBusiness);
  if (!user || !currentBusiness) {
    throw new Error('establishOfflineBusinessSession: invalid business');
  }

  kvSet('currentUser', user);
  kvSet('currentBusiness', currentBusiness);
  clearBusinessLogoutIntent();

  if (effectiveBusiness.manager_code) {
    kvSet('managerCode', effectiveBusiness.manager_code);
  }

  if (clearRole) {
    const { clearRoleSessionStorage } = await import('../utils/roleSessionLogout');
    clearRoleSessionStorage(effectiveBusiness.id);
  }

  const canonicalId = normalizeBusinessStorageId(effectiveBusiness.id);
  if (assignDefaultRole) {
    // Deprecated soft-skip: still write manager only if caller explicitly opts in.
    // Login must use assignDefaultRole:false so CodeEntry (PIN) always follows password.
    persistRoleForBusiness(canonicalId, {
      type: 'manager',
      name: effectiveBusiness.owner_name || 'Manager',
      loginAt: new Date().toISOString()
    });
  }

  // Unlock UI first — durable IDB persist must not block login paint
  if (dispatchAuthEvent && typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('authStateChanged', { detail: { user } }));
  }

  if (persist) {
    void Promise.all([
      kvSetPersisted('currentUser', user),
      kvSetPersisted('currentBusiness', currentBusiness),
      effectiveBusiness.manager_code
        ? kvSetPersisted('managerCode', effectiveBusiness.manager_code)
        : Promise.resolve()
    ]).catch((err) => {
      console.warn('[offlineAuth] session persist deferred failed:', err?.message || err);
    });
  }

  // Full payload bootstrap only when requested (registration). Login loads via DataContext.
  if (awaitBootstrap) {
    try {
      const { bootstrapNewBusinessStorage } = await import(
        '../infrastructure/storage/bootstrapNewBusinessStorage'
      );
      await bootstrapNewBusinessStorage(effectiveBusiness.id, effectiveBusiness);
    } catch (err) {
      console.warn('[offlineAuth] Business payload bootstrap failed:', err?.message || err);
    }
  }

  return { user, currentBusiness };
}

export function persistManagerCode(code) {
  if (code) kvSet('managerCode', code);
}

export function persistBusinessSessionPair(user, currentBusiness, options = {}) {
  if (!user || !currentBusiness) return;
  kvSet('currentUser', user);
  kvSet('currentBusiness', currentBusiness);
  clearBusinessLogoutIntent();
  if (options.managerCode) {
    kvSet('managerCode', options.managerCode);
  }
  if (options.dispatchAuthEvent && typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('authStateChanged', { detail: { user } }));
  }
}

export function switchStoredCurrentBusinessId(businessId) {
  if (!businessId) return;
  const business = readStoredCurrentBusiness() || {};
  kvSet('currentBusiness', {
    ...business,
    id: businessId,
    businessId
  });
}

export function persistSessionBusinessLogo(logo) {
  const user = readStoredSessionUser();
  const business = readStoredCurrentBusiness();
  if (user?.business) {
    kvSet('currentUser', {
      ...user,
      business: { ...user.business, logo: logo ?? null }
    });
  }
  if (business) {
    kvSet('currentBusiness', { ...business, logo: logo ?? null });
  }
}

function parseStoredKvJson(raw) {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'object') return raw;
  if (typeof raw === 'string') {
    if (raw === '[object Object]' || raw === '"[object Object]"' || raw.trim() === '') {
      return null;
    }
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  return null;
}

/** Shared session JSON parse (currentUser / currentBusiness). */
export function parseStoredSessionValue(raw) {
  return parseStoredKvJson(raw);
}

export function readStoredSessionUser() {
  const user = parseStoredKvJson(kvGet('currentUser'));
  return user ? normalizeStoredSessionUser(user) : null;
}

/** Sync business_type from session — avoids restaurant POS chrome flash before bootstrap. */
export function readStoredSessionBusinessType() {
  const user = readStoredSessionUser();
  if (!user) return null;
  if (user.type === 'business' && user.business?.business_type) {
    return user.business.business_type;
  }
  if (user.user?.user_metadata?.business_type) {
    return user.user.user_metadata.business_type;
  }
  if (user.user_metadata?.business_type) {
    return user.user_metadata.business_type;
  }
  return null;
}

export function readStoredCurrentBusiness() {
  return parseStoredKvJson(kvGet('currentBusiness'));
}

export function readStoredSessionBusiness() {
  const user = readStoredSessionUser();
  if (user?.type === 'business' && user.business) {
    return user.business;
  }
  return readStoredCurrentBusiness();
}

export function clearCorruptedSessionUser() {
  kvRemove('currentUser');
}

/**
 * Temporäre Admin-Übergangs-Session (lokaler Admin-Login).
 */
export function establishAdminTransitionSession() {
  const user = {
    type: 'admin',
    user: {
      id: 'admin-temp-user',
      email: 'admin@local.temp',
      user_metadata: {
        role: 'admin',
        temporary: true
      }
    },
    temporary: true,
    offline: true
  };
  const currentBusiness = {
    id: 'admin-temp-business',
    business_name: 'Admin Transition',
    name: 'Admin Transition'
  };

  kvSet('currentUser', user);
  kvSet('currentBusiness', currentBusiness);
  clearBusinessLogoutIntent();

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('authStateChanged', { detail: { user } }));
  }

  return { user, currentBusiness };
}
